import { useEffect, useState } from "react";

import { Destination, FormField } from "@prequel/react";
import { CopyToClipboard } from "@prequel/shared-components";

import { WrapperProps } from "../..";

import { MagicLink } from "@/types";
import { Source } from "@/types/sources";
import { SourceFormField } from "@/types/sources/form";

const SSHPublicKey = <F extends FormField | SourceFormField, C extends Destination | Source | MagicLink>({
  field,
  connection,
  setConnectionField,
  isEditing,
}: WrapperProps<F, C>) => {
  const [displayValue, setDisplayValue] = useState<string>();
  const destinationValue = connection[field.name];
  const fieldName = field.name;
  const fieldConst = field.const;

  useEffect(() => {
    // Core logical checks to avoid type complaints within the branch: confirms we are looking at the ssh_public_key field and we have the necessary values
    // debugger; // eslint-disable-line
    if (typeof fieldConst === "string") {
      if (isEditing) {
        // If we are editing and the destination does not have a key, display const and set the key on the destination
        if (!destinationValue) {
          setConnectionField(fieldName as keyof C, fieldConst);
          setDisplayValue(fieldConst);
        } else {
          // If we are editing and the destination has a key, display it
          setDisplayValue(destinationValue.toString());
        }
      } else {
        // If we're not editing, display the key and set it on the destination
        setConnectionField(fieldName as keyof C, fieldConst);
        setDisplayValue(fieldConst);
      }
    }
  }, [fieldConst, isEditing, destinationValue, fieldName, setConnectionField]);

  if (!displayValue) {
    return <></>;
  }

  // Don't display comments when we render the key, but leave them in the copyable value
  const truncatedKey = displayValue
    ?.replace("-----BEGIN PUBLIC KEY-----", "")
    .replace("-----END PUBLIC KEY-----", "")
    .replace("prequel-ssh-tunneling-public-key", "");

  return (
    <>
      <div
        className="
          mt-1 inline-flex w-full rounded-md border border-gray-300 bg-gray-50 py-2 pr-1 pl-3 font-mono text-sm
          text-gray-500 shadow-sm
        "
      >
        <div className="mr-3 w-full truncate">{truncatedKey}</div>
        <CopyToClipboard textToCopy={displayValue} />
      </div>
      {field.description && (
        <p className="mt-2 text-xs whitespace-pre-line text-gray-400" id="description">
          {field.description}
        </p>
      )}
    </>
  );
};

export default SSHPublicKey;
